'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/app/components/ui/dialog';
import type { ImagePromptWithSize, ImageRange } from '@/app/types';
import { cn } from '@/app/utils';
import { CropOptionDisplay } from './components/CropOptionDisplay';
import type { CropOption } from './use-crop-options';
import { useImgCropController } from './use-img-crop-controller';

type TemplateImgCropModalProps = {
  onClose: () => void;
  onConfirm: (range: ImageRange) => Promise<void>;
  imageSource: ImagePromptWithSize | null;
  cropOptions?: CropOption[];
  /** 不传时回落到 imgCropModal.title */
  title?: string;
  /** 不传时回落到 imgCropModal.description */
  description?: string;
  defaultCropIndex?: number;
  /** 首次打开与切比例都按所选比例居中最大化（头像场景开启）。 */
  maximizeToAspect?: boolean;
};

export const TemplateImgCropModal: FC<TemplateImgCropModalProps> = ({
  onClose,
  onConfirm,
  imageSource,
  cropOptions,
  title,
  description,
  defaultCropIndex = 0,
  maximizeToAspect = false,
}) => {
  const t = useTranslations();
  const {
    aspectRatios,
    selectedAspect,
    saving,
    imgRef,
    clipMaskDom,
    handleImageLoad,
    handleAspectChange,
    handleSaveCropImage,
    handleMoving,
    handleMoveEnd,
    canClose,
  } = useImgCropController({ imageSource, cropOptions, defaultCropIndex, maximizeToAspect, onConfirm, onClose });

  const resolvedTitle = title ?? t('imgCropModal.title');
  const resolvedDescription = description ?? t('imgCropModal.description');

  const handleClose = () => {
    if (!canClose()) return;
    onClose();
  };

  return (
    <Dialog open={Boolean(imageSource)} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[928px] md:h-4/5" variant="mobileFullScreen">
        {resolvedTitle && <DialogTitle>{resolvedTitle}</DialogTitle>}

        <div className="flex-1 md:flex md:flex-col md:overflow-hidden">
          {resolvedDescription && <DialogDescription className="mb-4">{resolvedDescription}</DialogDescription>}
          <div
            onPointerMove={handleMoving}
            onPointerLeave={handleMoveEnd}
            onPointerUp={handleMoveEnd}
            className="relative h-[486px] w-full select-none p-4 md:flex-1 md:overflow-hidden"
          >
            <img
              src={imageSource?.sourceImagePreview ?? ''}
              alt="image-source"
              id="crop-image-source"
              ref={imgRef}
              className="size-full object-contain"
              onLoad={handleImageLoad}
            />
            {clipMaskDom}
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-6">
            {aspectRatios.map((item) => (
              <div
                key={item.value}
                className={
                  aspectRatios.length === 1
                    ? 'flex items-center justify-center gap-2'
                    : cn(
                        'flex h-12 cursor-pointer items-center justify-center gap-2 rounded-full border border-system-white24 bg-[#0D0D1214] px-6 text-white',
                        selectedAspect === item.value
                          ? 'gradient-border-1 border-gradient-b-primary-dark'
                          : 'hover:border-system-white36',
                      )
                }
                onClick={() => handleAspectChange(item.value)}
              >
                <CropOptionDisplay option={item} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            className="h-9 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
            onClick={handleClose}
            disabled={saving}
          >
            {t('create.createForm.upload.cancel')}
          </Button>
          <Button
            variant="primary"
            className="h-9 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
            onClick={handleSaveCropImage}
            disabled={saving}
          >
            {saving && <Loading className="mr-2 animate-spin text-base" />}
            {t('create.createForm.upload.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
